import { CHANNELS } from "../../shared/channels";
import { locale, t } from "../i18n";
import { GOAL_COLOURS, Goal, GoalDraft, goalReadout, goalShare, goalStep } from "../../shared/goals";
import { call, listen } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast, renderList } from "../dom";
import { isViewActive, onViewShown } from "../navigation";
import { buzz } from "../tweens";
import { iconSvg } from "../icons";
import { onSharedChange, readSharedAll, sharedHead } from "../shares";

// goals as sticky notes: a bar, + and -, done crosses it out and keeps it, delete clears it

let goals: Goal[] = [];
let editing: Goal | null = null;
let editColour = GOAL_COLOURS[0];
// the one just ticked off gets its line drawn through once
let justDone = "";

function setColour(element: HTMLElement, colour: string): void {
  GOAL_COLOURS.forEach((item) => element.classList.remove(`sticky-${item}`));
  element.classList.add(`sticky-${colour}`);
}

function sayError(error: unknown): void {
  flashToast(t(error instanceof Error ? error.message : String(error)));
}

// the note is narrow on a phone, so the count and what's counted sit on two lines
function noteReadout(goal: Goal): { readout: string; unit: string } {
  if (goal.done) return { readout: t("done"), unit: new Date(goal.doneAt).toLocaleDateString(locale(), { day: "numeric", month: "short" }) };
  if (goal.unit === "%" && goal.target === 100) return { readout: goalReadout(goal), unit: "" };
  return { readout: `${goal.progress}/${goal.target}`, unit: goal.unit === "%" ? "" : goal.unit };
}

// with no actions it's someone else's goal, shown but never changed
export function buildGoalNote(goal: Goal, actions: GoalActions | null): HTMLElement {
  const note = fillFields(cloneTemplate("template-goal"), {
    title: goal.title,
    note: goal.note,
    ...noteReadout(goal)
  });
  setColour(note, goal.colour);
  note.style.setProperty("--fill", String(goalShare(goal)));
  note.classList.toggle("done", goal.done);
  note.classList.toggle("reached", !goal.done && goal.progress >= goal.target);
  note.classList.toggle("just-done", goal.id === justDone);
  note.classList.toggle("read-only", !actions);
  note.setAttribute("aria-label", `${goal.title}, ${goal.done ? t("done") : goalReadout(goal)}`);
  const key = (action: string) => note.querySelector<HTMLButtonElement>(`[data-action='${action}']`)!;
  const doneKey = key("done");
  doneKey.querySelector("span")!.textContent = goal.done ? t("undo") : t("done");
  doneKey.querySelector("i")!.innerHTML = iconSvg(goal.done ? "undo" : "check", 18);
  key("less").disabled = goal.done || goal.progress <= 0;
  key("more").disabled = goal.done || goal.progress >= goal.target;
  if (!actions) return note;
  const on = (action: string, run: () => void) => key(action).addEventListener("click", (event) => {
    event.stopPropagation();
    run();
  });
  on("less", () => actions.step(goal, -1));
  on("more", () => actions.step(goal, 1));
  on("done", () => actions.done(goal, !goal.done));
  on("edit", () => actions.edit(goal));
  on("delete", () => actions.remove(goal));
  note.addEventListener("click", () => actions.edit(goal));
  return note;
}

export interface GoalActions {
  step(goal: Goal, direction: number): void;
  done(goal: Goal, done: boolean): void;
  edit(goal: Goal): void;
  remove(goal: Goal): void;
}

const actions: GoalActions = {
  step(goal, direction) {
    // moves straight away, the server's answer settles it
    const progress = Math.min(goal.target, Math.max(0, goal.progress + direction * goalStep(goal)));
    if (progress === goal.progress) return;
    buzz();
    goals = goals.map((item) => item.id === goal.id ? { ...item, progress } : item);
    render();
    if (progress >= goal.target) flashToast(t("{title} reached, tap done to cross it off", { title: goal.title }));
    void call<Goal[]>(CHANNELS.goalsStep, goal.id, direction).then(take).catch((error) => {
      sayError(error);
      void reload();
    });
  },
  done(goal, done) {
    buzz(done ? 14 : 6);
    justDone = done ? goal.id : "";
    void call<Goal[]>(CHANNELS.goalsDone, goal.id, done).then((list) => {
      take(list);
      if (done) flashToast(t("{title} done", { title: goal.title }));
    }).catch(sayError);
  },
  edit: (goal) => openEditor(goal),
  remove(goal) {
    if (!window.confirm(t("delete {title}? it won't come back", { title: goal.title }))) return;
    void call<Goal[]>(CHANNELS.goalsRemove, goal.id).then((list) => {
      if (editing?.id === goal.id) closeEditor();
      take(list);
    }).catch(sayError);
  }
};

function take(list: Goal[]): void {
  goals = list;
  render();
}

function render(): void {
  const open = goals.filter((goal) => !goal.done);
  const done = goals.filter((goal) => goal.done);
  renderList(byId("goals-open"), open, (goal) => buildGoalNote(goal, actions));
  renderList(byId("goals-done"), done, (goal) => buildGoalNote(goal, actions));
  byId("goals-done-head").textContent = t("done · {n}", { n: done.length });
  byId("goals-done-box").hidden = done.length === 0;
  byId("goals-empty").hidden = goals.length > 0;
  justDone = "";
}

async function reload(): Promise<void> {
  take(await call<Goal[]>(CHANNELS.goalsList));
  await reloadShared();
}

async function reloadShared(): Promise<void> {
  const people = await readSharedAll<Goal[]>("goals", []);
  const box = byId("goals-shared");
  box.hidden = people.length === 0;
  box.replaceChildren(...people.map((person) => {
    const section = document.createElement("div");
    section.className = "stack shared-box";
    const grid = document.createElement("ul");
    grid.className = "sticky-grid goal-grid";
    renderList(grid, person.data, (goal) => buildGoalNote(goal, null));
    section.append(sharedHead(person.name, "goals"), grid);
    if (!person.data.length) {
      const empty = document.createElement("p");
      empty.className = "empty";
      empty.textContent = t("no goals");
      section.append(empty);
    }
    return section;
  }));
}

// the editor is a sticky note too, in the colour it'll be
const form = () => byId<HTMLFormElement>("goals-form");
const progressInput = () => byId<HTMLInputElement>("goals-progress");

function readTarget(): number {
  const value = Math.round(Number(byId<HTMLInputElement>("goals-target").value));
  return Number.isFinite(value) && value >= 1 ? Math.min(value, 1_000_000) : 100;
}

function readUnit(): string {
  const unit = byId<HTMLInputElement>("goals-unit").value.trim();
  return unit || (byId<HTMLInputElement>("goals-target").value.trim() ? "" : "%");
}

function paintEditor(): void {
  const target = readTarget();
  const slider = progressInput();
  slider.max = String(target);
  const progress = Math.min(target, Number(slider.value) || 0);
  slider.value = String(progress);
  byId("goals-readout").textContent = goalReadout({ progress, target, unit: readUnit() });
  form().style.setProperty("--fill", String(target ? progress / target : 0));
  setColour(form(), editColour);
  byId("goals-colours").querySelectorAll<HTMLButtonElement>(".goal-swatch").forEach((swatch) => {
    const on = swatch.dataset.colour === editColour;
    swatch.classList.toggle("active", on);
    swatch.setAttribute("aria-checked", String(on));
  });
}

export function openEditor(goal: Goal | null): void {
  editing = goal;
  editColour = goal?.colour || GOAL_COLOURS[goals.filter((item) => !item.done).length % GOAL_COLOURS.length];
  byId<HTMLInputElement>("goals-title").value = goal?.title || "";
  byId<HTMLTextAreaElement>("goals-note").value = goal?.note || "";
  const percent = !goal || (goal.target === 100 && goal.unit === "%");
  byId<HTMLInputElement>("goals-target").value = percent ? "" : String(goal.target);
  byId<HTMLInputElement>("goals-unit").value = percent ? "" : goal.unit;
  progressInput().max = String(goal?.target || 100);
  progressInput().value = String(goal?.progress || 0);
  byId("goals-delete").hidden = !goal;
  byId("goals-status").textContent = goal?.done ? t("this one's done, saving keeps it crossed out") : "";
  paintEditor();
  form().hidden = false;
  form().scrollIntoView({ block: "nearest", behavior: "smooth" });
  if (!goal) byId<HTMLInputElement>("goals-title").focus();
}

function closeEditor(): void {
  editing = null;
  form().hidden = true;
}

async function save(event: Event): Promise<void> {
  event.preventDefault();
  const draft: GoalDraft = {
    id: editing?.id,
    title: byId<HTMLInputElement>("goals-title").value,
    note: byId<HTMLTextAreaElement>("goals-note").value,
    colour: editColour,
    target: readTarget(),
    unit: readUnit(),
    progress: Number(progressInput().value) || 0
  };
  try {
    take(await call<Goal[]>(CHANNELS.goalsSave, draft));
    closeEditor();
  } catch (error) {
    byId("goals-status").textContent = t(error instanceof Error ? error.message : String(error));
  }
}

function buildSwatches(): void {
  const box = byId("goals-colours");
  box.replaceChildren(...GOAL_COLOURS.map((colour) => {
    const swatch = document.createElement("button");
    swatch.type = "button";
    swatch.className = `goal-swatch sticky-${colour}`;
    swatch.dataset.colour = colour;
    swatch.setAttribute("role", "radio");
    swatch.setAttribute("aria-label", t(colour));
    swatch.addEventListener("click", () => {
      editColour = colour;
      paintEditor();
    });
    return swatch;
  }));
}

export function setupGoalsView(): void {
  buildSwatches();
  byId("goals-new").addEventListener("click", () => openEditor(null));
  byId("goals-cancel").addEventListener("click", closeEditor);
  byId("goals-delete").addEventListener("click", () => {
    if (editing) actions.remove(editing);
  });
  form().addEventListener("submit", save);
  progressInput().addEventListener("input", paintEditor);
  byId("goals-target").addEventListener("input", paintEditor);
  byId("goals-unit").addEventListener("input", paintEditor);
  listen(CHANNELS.goalsUpdated, () => {
    if (isViewActive("goals")) void reload().catch(() => undefined);
  });
  onSharedChange("goals", () => {
    if (isViewActive("goals")) void reloadShared().catch(() => undefined);
  });
  onViewShown("goals", () => void reload().catch(sayError));
}
